import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { ExtensionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { ExtensionDetailView } from 'src/app/pages/extension-details/models/extension-detail-view.model';
import { ExtensionTypeOption } from 'src/app/pages/extension-register/models/extension-type-option.interface';

@Component({
    selector: 'ndt-add-account-extension-view',
    styleUrls: ['./add-account-extension-view.component.scss'],
    templateUrl: './add-account-extension-view.component.html',
    standalone: false
})
export class AddAccountExtensionViewComponent implements OnDestroy, OnInit {
   @Output() formSubmitted = new EventEmitter<any>();

   @Output() formCanceled = new EventEmitter<void>();

   @Output() setupKeysClicked = new EventEmitter<void>();

   @Input() callState: CallState;

   @Input() form: UntypedFormGroup;

   @Input() maxTimeoutValue: number;

   @Input() minTimeoutValue: number;

   @Input() existingExtensions: ExtensionDetailView[];

   @Input() isEdit: boolean;

   @ViewChild('nameInput') nameInput: ElementRef;

   destroyed$ = new Subject<void>();

   extensionTypes: typeof ExtensionTypes = ExtensionTypes;

   extensionTypesToInclude = [
      ExtensionTypes.AutoAttendant,
      ExtensionTypes.Fax,
      ExtensionTypes.HuntGroup,
      ExtensionTypes.VoiceMailbox,
      ExtensionTypes.ExternalForward,
   ];

   get selectedExtensionType(): ExtensionTypes | null {
      const formValue: ExtensionTypeOption | null = this.form.get('type')?.value;
      if (formValue) {
         return ExtensionTypes[formValue.name];
      } else {
         return null;
      }
   }

   ngOnInit() {
      this.setFormValidators(this.form.get('type')?.value);
      this.form
         .get('type')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((type: ExtensionTypeOption) => {
            if (type) {
               this.resetExtensionSpecificForm();
               this.setFormValidators(type);
            }
         });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(continuous = false): void {
      if (this.form.valid) {
         this.formSubmitted.emit({
            extension: { ...this.form.value },
            continuous,
         });
         this.nameInput.nativeElement.focus();
         this.form.markAsPristine();
      } else {
         this.form.markAllAsTouched();
      }
   }

   setFormValidators(type: ExtensionTypeOption) {
      switch (ExtensionTypes[type.name]) {
         case ExtensionTypes.AutoAttendant:
            // TODO: removing this requirement for now as the timeout for auto attendants also requires an extension
            // this.minTimeoutValue = 1;
            // this.maxTimeoutValue = 10;
            // this.form
            //    .get('timeout')
            //    ?.setValidators([
            //       Validators.required
            //       Validators.min(this.minTimeoutValue),
            //       Validators.max(this.maxTimeoutValue),
            //    ]);
            // this.form.get('timeout')?.updateValueAndValidity();
            break;
         case ExtensionTypes.HuntGroup:
            this.minTimeoutValue = 0;
            this.maxTimeoutValue = 59;
            this.form
               .get('timeout')
               ?.setValidators([
                  Validators.required,
                  Validators.min(this.minTimeoutValue),
                  Validators.max(this.maxTimeoutValue),
               ]);
            this.form.get('timeout')?.updateValueAndValidity();
            break;
         case ExtensionTypes.ExternalForward:
            this.form.get('forwardTo')?.setValidators([Validators.required, phoneNumberValidator()]);
            this.form.get('forwardTo')?.updateValueAndValidity();
            break;
         case ExtensionTypes.Fax:
         case ExtensionTypes.VoiceMailbox:
            this.form
               .get('email')
               ?.setValidators([
                  Validators.required,
                  Validators.pattern(
                     /^(([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))*)?$/i
                  ),
               ]);
            this.form.get('email')?.updateValueAndValidity();
      }
   }
   resetExtensionSpecificForm() {
      this.form.get('timeout')?.clearValidators();
      this.form.get('timeout')?.setValue(null);
      this.form.get('email')?.clearValidators();
      this.form.get('email')?.setValue(null);
      this.form.get('forwardTo')?.clearValidators();
      this.form.get('forwardTo')?.setValue(null);
   }

   onSetupKeys() {
      this.setupKeysClicked.emit();
   }
}
